/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */


import {SpeciesTableDeltaColumnWrapper} from "./SpeciesTableDeltaColumnWrapper";
import {SpeciesTableLatinNameColumn} from "./SpeciesTableLatinNameColumn";
import React from "react";
import {SpeciesTableDeltaColumn} from "./SpeciesTableDeltaColumn";
import {service as SpeciesTypeService} from "../../../../../Services/SpeciesTypeService";
import SpeciesTypeIcon from "../../../../../../../monitoring/src/Components/CustomIcons/SpeciesTypeIcon";
import {ServiceDrivenTableDeltaColumn} from "./ServiceDrivenTableDeltaColumn";
import {service as SpeciesNativityService} from "../../../../../Services/SpeciesNativityService";
import {service as SpeciesInvasivityService} from "../../../../../Services/SpeciesInvasivityService";
import {service as SpeciesRequestStatusService} from "../../../../../Services/SpeciesRequestStatusService";
import {service as UserService} from "../../../../../Services/UserService";
import {DateTableColumn, ServiceDrivenTableColumn} from "../../../../Common";
import {SpeciesRequestActionTableColumn} from "./SpeciesRequestActionTableColumn";
import {UserTableColumn} from "../../../../Common/Tables/UserTableColumn";

// Helper functions
const getIsDefaultValue = (value) => {
	if(Boolean(value) === false) {
		return "No";
	} else if(Boolean(value ?? true) === true) {
		return "Yes";
	}
}

const getSearchableValue = (searchableValue) => {
	if(String(searchableValue) === '0') {
		return "Hidden";
	} else if(String(searchableValue ?? 1) === '1') {
		return "Visible";
	}
}

// Global Species Table
export const scientificName = {
	title: "Scientific Name",
	key: "scientificName",
	sortable: true,
	logic: x => ({
		modifiedValue: x?.SpeciesListSpecies?.alternativeLatinName,
		nonModifiedValue: x?.latinName,
	}),
	sortLogic: x => (
		x?.SpeciesListSpecies?.alternativeLatinName ?? x?.latinName
	),
	cellRenderer: SpeciesTableDeltaColumnWrapper,
	renderer: SpeciesTableLatinNameColumn,
};

export const commonName = {
	title: "Common Name",
	key: "commonName",
	sortable: true,
	logic: x => ({
		modifiedValue: x?.SpeciesListSpecies?.alternativeCommonName,
		nonModifiedValue: x?.commonName,
	}),
	sortLogic: x => (
		x?.SpeciesListSpecies?.alternativeCommonName ?? x?.commonName
	),
	cellRenderer: SpeciesTableDeltaColumnWrapper,
	renderer: SpeciesTableDeltaColumn
};

export const growthForm = {
	title: "Growth Form",
	key: "growthForm",
	sortable: true,
	logic: x => ({
		service: SpeciesTypeService,
		modifiedId: x?.SpeciesListSpecies?.speciesTypeId,
		nonModifiedId: x?.speciesTypeId,
		iconComponent: <SpeciesTypeIcon className="sb-species-type-icon-in-table" speciesTypeId={x?.SpeciesListSpecies?.speciesTypeId ?? x?.speciesTypeId} />
	}),
	sortLogic: x => (
		SpeciesTypeService.CachedTitleById(x?.SpeciesListSpecies?.speciesTypeId ?? x?.speciesTypeId)
	),
	cellRenderer: SpeciesTableDeltaColumnWrapper,
	renderer: ServiceDrivenTableDeltaColumn,
};

export const nativity = {
	title: "Nativity",
	key: "speciesNativity",
	sortable: true,
	logic: x => ({
		service: SpeciesNativityService,
		modifiedId: x?.SpeciesListSpecies?.speciesNativityId,
		nonModifiedId: x?.speciesNativityId,
	}),
	sortLogic: x => (
		SpeciesNativityService.CachedTitleById(x?.SpeciesListSpecies?.speciesNativityId ?? x?.speciesNativityId)
	),
	cellRenderer: SpeciesTableDeltaColumnWrapper,
	renderer: ServiceDrivenTableDeltaColumn,
};

export const invasivity = {
	title: "Invasivity",
	key: "speciesInvasivity",
	sortable: true,
	logic: x => ({
		service: SpeciesInvasivityService,
		modifiedId: x?.SpeciesListSpecies?.speciesInvasivityId,
		nonModifiedId: x?.speciesInvasivityId,
	}),
	sortLogic: x => (
		x?.SpeciesListSpecies?.speciesInvasivityId ?? x?.speciesInvasivityId
	),
	cellRenderer: SpeciesTableDeltaColumnWrapper,
	renderer: ServiceDrivenTableDeltaColumn,
};

export const visibility = {
	title: "Visibility",
	key: "search",
	sortable: true,
	logic: x => ({
		modifiedValue: getSearchableValue(x?.SpeciesListSpecies?.searchable),
		nonModifiedValue: getSearchableValue(x?.SpeciesListSpecies?.searchable ?? 0),
	}),
	sortLogic: x => (
		getSearchableValue(x?.SpeciesListSpecies?.searchable ?? x?.searchable)
	),
	cellRenderer: SpeciesTableDeltaColumnWrapper,
	renderer: SpeciesTableDeltaColumn
};

export const isDefault = {
	title: "Default",
	key: "isDefault",
	sortable: true,
	logic: x => ({
		modifiedValue: getIsDefaultValue(x?.isDefault),
		nonModifiedValue: getIsDefaultValue(x?.isDefault),
	}),
	sortLogic: x => (
		getIsDefaultValue(x?.isDefault)
	),
	cellRenderer: SpeciesTableDeltaColumnWrapper,
	renderer: SpeciesTableDeltaColumn
};

// SpeciesRequest Table
export const requester = {
	title: "Requester",
	key: "requestedByUserId",
	sortable: true,
	logic: x => x.SpeciesRequest?.requestedByUserId,
	sortLogic: x => UserService.TitleById(x.requestedByUserId),
	renderer: UserTableColumn,
	rendererProps: { showProfileImage: false },
}

export const dateRequested = {
	title: "Date Requested",
	key: "requested",
	sortable: true,
	logic: x => x.SpeciesRequest?.requested,
	headerClassName: "short-date",
	renderer: DateTableColumn
}

export const speciesRequestStatus = {
	title: "Status",
	key: "speciesRequestStatusId",
	sortable: true,
	logic: x => x.SpeciesRequest?.speciesRequestStatusId,
	sortLogic: x => x.SpeciesRequest?.speciesRequestStatusId,
	renderer: ServiceDrivenTableColumn(SpeciesRequestStatusService),
}

export const reviewAction = {
	title: "Action",
	key: "action",
	sortable: false,
	logic: (x) => x,
	cellClassName: "actions-cell",
	headerClassName: "actions-cell",
	renderer: SpeciesRequestActionTableColumn,
}
